/**
 * <TutorEngagementBar> — le score d'engagement d'un élève, sur sa carte de bilan et dans
 * l'historique (molecule).
 *
 * L'écran d'aujourd'hui (`TappableEngagementBar` + `AxisDrillDown` de `StudentCloseOutCard`) :
 * « Score d'engagement » et le total sur 100 (vert à partir de 60, orange de 40 à 59, rouge
 * dessous), la barre des trois axes, puis chaque axe — Présence, Participation, Productivité —
 * tel que le rapport de séance le montre déjà (`<LiveEngagementScore>` : son score, et son détail
 * critère par critère qu'on déplie).
 *
 * **La productivité se calcule après la clôture** : tant qu'elle n'a pas atterri, le total se tait
 * (« — »), son segment reste vide, et l'axe laisse la place à un encadré qui le dit (`<AlertCard>`)
 * — un « 0/30 » se lirait comme un résultat. Quand le score oral ne viendra plus, un encadré dit
 * que le score ne reflète que l'écrit.
 */
import React from "react"

import { Row } from "../../atoms/containers/Row"
import { Stack } from "../../atoms/containers/Stack"
import { SegmentedBar } from "../../atoms/SegmentedBar"
import { Text } from "../../atoms/typography/Text"
import { type SemanticColorKey, textInkOnFill } from "../../theme/tokens/colors"
import { AlertCard } from "../AlertCard"
import { LiveEngagementScore } from "../LiveEngagementScore"
import { type UITutorEngagement } from "../../../ports/primary/ui/types/tutorCloseOut"

export type TutorEngagementBarProps = {
    engagement: UITutorEngagement
    testID?: string
}

const TEXTS = {
    title: "Score d'engagement",
    pendingTitle: "Productivité · calcul en cours…",
    pendingDetail: "Le score oral est en cours de calcul. Le détail par critère s'affichera dès qu'il sera disponible.",
    failedDetail: "Score oral indisponible — le score ci-dessus ne reflète que le travail écrit.",
}

/** Le seuil de couleur du total — celui de l'écran d'aujourd'hui (`SCORE_COLOR`). */
function totalColor(total: number): SemanticColorKey {
    if (total >= 60) return "success"
    if (total >= 40) return "warning"
    return "danger"
}

export function TutorEngagementBar(props: TutorEngagementBarProps) {
    const { engagement, testID } = props
    const pending = engagement.productivity === "pending"
    const total = pending || engagement.total == null ? null : Math.round(engagement.total)

    return (
        <Stack gap="2" testID={testID}>
            <Row justify="between" align="center">
                <Text size="text-sm" color="text-muted">
                    {TEXTS.title}
                </Text>
                <Text
                    size="text-md"
                    weight="bold"
                    color={total == null ? "text-muted" : textInkOnFill(totalColor(total))}
                >
                    {total == null ? "—" : `${total}/100`}
                </Text>
            </Row>
            <SegmentedBar
                max={100}
                height={6}
                segments={engagement.axes.map((axis) => ({
                    value: pending && axis.key === "productivity" ? 0 : Math.max(0, Math.min(100, axis.value)),
                    color: axis.color,
                }))}
            />
            {/* `key` sort de l'axe avant l'étalement : React refuse une prop `key` étalée. */}
            {engagement.axes.map(({ key, ...axis }) =>
                pending && key === "productivity" ? (
                    <AlertCard key={key} severity="info" title={TEXTS.pendingTitle} message={TEXTS.pendingDetail} />
                ) : (
                    <LiveEngagementScore key={key} {...axis} />
                )
            )}
            {engagement.productivity === "failed" && <AlertCard severity="warning" message={TEXTS.failedDetail} />}
        </Stack>
    )
}
